import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Cacheable } from 'ts-cacheable';

import { ApiService } from '../../core/api';
import { ResponseDatas } from '../../core/api/types';
import { AuthService } from '../../core/auth';

import {
  Menus,
  PlatformSource,
  RegisterUserRequest,
  RegisterUserResponse,
  ResetPasswordRequest,
  SetNewPasswordRequest,
  SMSAuthenticationRequest,
  SMSCodeRequest,
  UserChecklist,
  UserLanguage
} from './types';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private readonly apiService = inject(ApiService);

  @Cacheable({
    cacheBusterObserver: AuthService.cacheBuster$
  })
  getSupportedLanguages(): Observable<UserLanguage[]> {
    return this.apiService
      .get<ResponseDatas<UserLanguage[]>>('Users/GetSupportedLanguages')
      .pipe(map((result) => result?.datas));
  }

  getChecklist(userID: number): Observable<UserChecklist> {
    return this.apiService.get<UserChecklist>(`Users/checklist/${userID}`);
  }

  getMenus(): Observable<Menus> {
    return this.apiService.get<Menus>('users/menus');
  }

  register(payload: RegisterUserRequest): Observable<RegisterUserResponse> {
    return this.apiService.postUnsecure<RegisterUserResponse>('users/Register', payload);
  }

  resetPassword(payload: ResetPasswordRequest): Observable<void> {
    return this.apiService.postUnsecure('users/ResetPass', payload);
  }

  resetPasswordForUser(userID: number, source: PlatformSource): Observable<void> {
    return this.apiService.get(`users/ResetPass/${userID}`, { params: { source } });
  }

  setNewPassword(payload: SetNewPasswordRequest): Observable<void> {
    return this.apiService.postUnsecure('users/ResetPass2', payload);
  }

  smsAuth(payload: SMSAuthenticationRequest): Observable<void> {
    return this.apiService.postUnsecure('Users/sms/auth', payload);
  }

  smsCodeRequest(payload: SMSCodeRequest): Observable<void> {
    return this.apiService.postUnsecure('Users/sms/request', payload);
  }
}
